import { clsx } from "@mantine/core";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

interface ClaimsFilterProps {
    offerId: string;
    dateFrom: string;
    dateTo: string;
    onFilterChange: (filters: {
        offerId: string;
        dateFrom: string;
        dateTo: string;
    }) => void;
    onClearFilters: () => void;
}

const ClaimsFilter: FC<ClaimsFilterProps> = ({
    offerId,
    dateFrom,
    dateTo,
    onFilterChange,
    onClearFilters,
}) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const handleInputChange = (field: string, value: string) => {
        onFilterChange({
            offerId,
            dateFrom,
            dateTo,
            [field]: value,
        });
    };

    const hasActiveFilters = offerId || dateFrom || dateTo;

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-2">
                    <svg
                        className="w-5 h-5 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                        />
                    </svg>
                    <span className="font-medium text-gray-700">
                        {t("claims_page.filters.title")}
                    </span>
                    {hasActiveFilters && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t("claims_page.filters.active")}
                        </span>
                    )}
                </div>
                <svg
                    className={clsx(
                        "w-5 h-5 text-gray-400 transition-transform",
                        isExpanded && "rotate-180",
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                        <div>
                            <label
                                htmlFor="offerId"
                                className="block text-sm font-medium text-gray-700 mb-1">
                                {t("claims_page.filters.policy_id")}
                            </label>
                            <input
                                type="text"
                                id="offerId"
                                value={offerId}
                                onChange={(e) =>
                                    handleInputChange("offerId", e.target.value)
                                }
                                placeholder={t("placeholders.enter_policy_id")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="dateFrom"
                                className="block text-sm font-medium text-gray-700 mb-1">
                                {t("claims_page.filters.date_from")}
                            </label>
                            <input
                                type="date"
                                id="dateFrom"
                                value={dateFrom}
                                onChange={(e) =>
                                    handleInputChange(
                                        "dateFrom",
                                        e.target.value,
                                    )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="dateTo"
                                className="block text-sm font-medium text-gray-700 mb-1">
                                {t("claims_page.filters.date_to")}
                            </label>
                            <input
                                type="date"
                                id="dateTo"
                                value={dateTo}
                                onChange={(e) =>
                                    handleInputChange("dateTo", e.target.value)
                                }
                                min={dateFrom || undefined}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                            />
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={onClearFilters}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors">
                                {t("claims_page.filters.clear_all")}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClaimsFilter;
